import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Get user ID from localStorage or session
 * Returns userId or guest ID
 */
function getUserId() {
  // Try to get from localStorage (auth token)
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch (e) {
      console.error('Error parsing token:', e);
    }
  }

  // Check for guest ID
  const guestId = localStorage.getItem('tvent_guest_id');
  return guestId || null;
}

/**
 * Save onboarding preferences to backend (logged-in users) or localStorage (guests)
 * @param {string[]} selectedTags - Array of selected tag IDs
 * @param {string} lifestyleText - User's lifestyle description
 * @param {string} language - Selected language (TH, EN, CN)
 * @returns {Promise<{success: boolean, keywordsExtracted?: number, error?: string}>}
 */
export async function saveOnboardingPreferences(selectedTags, lifestyleText, language = 'EN') {
  const userId = getUserId();

  console.log('💾 [SavePreferences] Saving onboarding preferences:', {
    userId: userId || 'none',
    tagsCount: selectedTags?.length || 0,
    hasLifestyleText: !!lifestyleText,
    language
  });

  // Validate inputs
  if (!selectedTags || selectedTags.length < 2 || selectedTags.length > 10) {
    console.error('❌ [SavePreferences] Invalid tag selection:', selectedTags);
    return {
      success: false,
      error: 'Must select 2-10 tags'
    };
  }

  // Check if user is logged in
  if (userId && !userId.startsWith('guest')) {
    // Logged-in user - save to backend
    try {
      const response = await axios.post(
        `${API_URL}/api/users/${userId}/onboarding`,
        {
          selectedTags,
          lifestyleText: lifestyleText || '',
          language
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('✅ [SavePreferences] Saved to backend:', response.data);

      return {
        success: true,
        keywordsExtracted: response.data.keywordsExtracted
      };
    } catch (error) {
      console.error('❌ [SavePreferences] Backend save failed:', error);

      // Fall back to localStorage if backend fails
      saveToLocalStorage(selectedTags, lifestyleText, language);

      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  } else {
    // Guest user - save to localStorage for later migration
    saveToLocalStorage(selectedTags, lifestyleText, language);

    console.log('✅ [SavePreferences] Saved to localStorage (guest mode)');

    return {
      success: true
    };
  }
}

/**
 * Save preferences to localStorage (for guests or backup)
 */
function saveToLocalStorage(selectedTags, lifestyleText, language) {
  const preferences = {
    tags: selectedTags,
    lifestyle: lifestyleText || '',
    language,
    timestamp: Date.now()
  };

  localStorage.setItem('tvent_temp_preferences', JSON.stringify(preferences));
  console.log('💾 [SavePreferences] Stored in localStorage:', preferences);
}

/**
 * Get stored preferences from localStorage
 * @returns {{tags: string[], lifestyle: string, language: string, timestamp: number} | null}
 */
export function getStoredPreferences() {
  const stored = localStorage.getItem('tvent_temp_preferences');
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch (e) {
    console.error('Error parsing stored preferences:', e);
    return null;
  }
}

/**
 * Clear stored preferences from localStorage
 */
export function clearStoredPreferences() {
  localStorage.removeItem('tvent_temp_preferences');
  console.log('🗑️ [SavePreferences] Cleared localStorage preferences');
}

/**
 * Check if user has completed onboarding
 * @returns {boolean}
 */
export function hasCompletedOnboarding() {
  return localStorage.getItem('tvent_onboarding_completed') === 'true';
}

/**
 * Mark onboarding as completed
 */
export function markOnboardingCompleted() {
  localStorage.setItem('tvent_onboarding_completed', 'true');
  localStorage.setItem('tvent_onboarding_completed_at', new Date().toISOString());
  console.log('✅ [SavePreferences] Onboarding marked as completed');
}

/**
 * Reset onboarding status (for testing)
 */
export function resetOnboarding() {
  localStorage.removeItem('tvent_onboarding_completed');
  localStorage.removeItem('tvent_onboarding_completed_at');
  localStorage.removeItem('tvent_temp_preferences');
  console.log('🔄 [SavePreferences] Onboarding reset');
}
